import React, { useEffect, useRef, Suspense } from "react";
import { resolveApiBaseUrl } from "@/utils/apiBase";
import {
  BrowserRouter,
  MemoryRouter,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAuthStore from "@/store/useAuthStore";
import AppRouter, { LoadingFallback } from "./router";
import {
  initNativePush,
  isNativePushSupported,
  resyncTokenForCurrentUser,
  requestNotificationPermissionOnBoot,
} from "@/services/nativePush";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import LocaleResolver from "@/components/LocaleResolver";
import { NetworkProvider } from "@/contexts/NetworkProvider";
import { initGeoListeners } from "@/services/nativeGeolocation";
import { useCarAutoSync } from "@/hooks/useCarAutoSync";
import { useCarCommand } from "@/hooks/useCarCommand";
import { usePresenceHeartbeat } from "@/hooks/infrastructure/usePresenceHeartbeat";
import {
  CAR_COMMAND_DISCARD_RECORDING,
  CAR_COMMAND_START_RECORDING,
  CAR_COMMAND_STOP_AND_SAVE,
} from "@/services/carBridge";
import { dispatchCarCommand } from "@/services/carCommandRouter";
import { discardRecordingOutsidePage } from "@/services/carCommandFallback";
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import { initDeviceFingerprint } from "@/utils/deviceFingerprint";
import { preloadCriticalData } from "@/services/offlineCache";
import { startMemoryPressureBridge } from "@/services/memoryPressure";
// GOOGLE PLAY QUALITY 2027 (Fase 3): Zero-Tap Sign-In (Restore Credentials)
import {
  registerRestoreKey,
  tryRestoreSession,
} from "@/services/restoreCredential";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import MaintenanceScreen from "@/components/MaintenanceScreen";
import ScrollToTop from "@/components/ScrollToTop";
import BiometricLockGate from "@/components/BiometricLockGate";

// Su WebView Capacitor (Android/iOS) la History API del browser non funziona
// in modo affidabile: gli URL come `/dashboard` vengono risolti come file fisici
// dentro il bundle e finiscono in 404 / schermata nera. Su native usiamo
// MemoryRouter (la cronologia vive solo in memoria del processo).

// Altezza banner impersonazione (px) — usata anche dal Navbar per offset top
export const IMPERSONATION_BANNER_H = 37;

// ─── Banner Impersonazione ────────────────────────────────────────────────────────────
//
// Visibile solo quando un admin sta impersonando un utente.
// Mostra l'email dell'utente impersonato e un pulsante per tornare
// al pannello admin, ripristinando il token e la sessione originali.
//
function ImpersonationBanner() {
  const { impersonatedUser, stopImpersonation } = useAuthStore();
  const navigate = useNavigate();

  if (!impersonatedUser) return null;

  const handleStop = () => {
    stopImpersonation();
    navigate("/admin/ops");
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: IMPERSONATION_BANNER_H,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        background: "#6b21a8",
        color: "#fff",
        fontSize: "13px",
        fontWeight: 500,
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
      }}
    >
      <span>
        👤 Modalità impersonazione attiva —{" "}
        <strong>{impersonatedUser.email}</strong>
      </span>
      <button
        onClick={handleStop}
        style={{
          background: "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: "6px",
          color: "#fff",
          cursor: "pointer",
          fontSize: "12px",
          fontWeight: 600,
          padding: "4px 12px",
        }}
      >
        🔙 Torna Admin
      </button>
    </div>
  );
}

// ─── Wrapper che compensa l'altezza del banner ────────────────────────────────────────
function AppWrapper() {
  const impersonatedUser = useAuthStore((s) => s.impersonatedUser);
  const bannerOffset = impersonatedUser ? IMPERSONATION_BANNER_H : 0;

  const user = useAuthStore((s) => s.user);

  // ANDROID AUTO (Ott 2026): mantiene allineati i percorsi mostrati sul
  // display di auto/moto. Gating Premium: l'esperienza in auto è riservata a
  // chi è abbonato o in prova (altrimenti l'auto mostra l'invito a passare a
  // Premium); al logout lo snapshot viene cancellato.
  useCarAutoSync(user?.id ?? null, user?.nickname ?? user?.username ?? null);

  // ANDROID AUTO (Ott 2026): esegue i comandi toccati sul display del veicolo.
  // "Avvia registrazione" riusa la strada del one-tap recording
  // (/routes/record?autoStart=1): stessa logica GPS/notifiche del telefono.
  // "Ferma e salva" può arrivare con il nome digitato sul display (name).
  //
  // FIX 14 Ott 2026 ("Ferma e salva non salvava il percorso"): il comando
  // nativo è già stato consumato in modo DISTRUTTIVO, quindi non può mai
  // sparire in silenzio. Prima veniva solo emesso un evento window: se la
  // pagina di registrazione non era montata (app riaperta a freddo, WebView
  // ricreata dal sistema, utente su un'altra pagina) nessuno lo ascoltava e il
  // giro andava perso senza avvisi. Ora la consegna passa dal router
  // (`dispatchCarCommand`) e, se nessuno può eseguirlo, si applica un
  // fallback esplicito per ciascun comando.
  const navigate = useNavigate();
  const handleCarCommand = React.useCallback(
    (command: string, name?: string | null) => {
      // Log diagnostico: Capacitor inoltra i console.* al logcat (tag
      // "Capacitor/Console"), utile per verificare il comando toccato in auto.
      console.info("[car] comando dal display ricevuto:", command, name ?? "");
      // 1) La pagina di registrazione, se montata, conosce la sessione in
      //    corso (e i suoi dati): è lei a eseguire start/stop/annulla.
      if (dispatchCarCommand(command, name)) return;
      // 2) Nessuno ha potuto eseguirlo: fallback, mai perso.
      if (command === CAR_COMMAND_START_RECORDING) {
        navigate("/routes/record?autoStart=1");
        return;
      }
      if (command === CAR_COMMAND_STOP_AND_SAVE) {
        // La pagina recupera la sessione da IndexedDB (flush ogni 10 fix) e
        // completa il salvataggio con il nome digitato in auto.
        const query = name ? `&carName=${encodeURIComponent(name)}` : "";
        navigate(`/routes/record?carSave=1${query}`);
        return;
      }
      if (command === CAR_COMMAND_DISCARD_RECORDING) {
        // Fuori pagina l'annullamento si esegue qui: ferma il watch nativo e
        // cancella la sessione persistita (niente registrazioni zombie).
        void discardRecordingOutsidePage();
      }
    },
    [navigate],
  );
  useCarCommand(handleCarCommand, !!user);
  usePresenceHeartbeat();

  return (
    <div style={{ paddingTop: bannerOffset }}>
      <LocaleResolver />
      <OnboardingFlow />
      <CookieConsentBanner />
      <Suspense fallback={<LoadingFallback />}>
        <AppRouter />
      </Suspense>
    </div>
  );
}

// ─── Native Bridge Handler — deep link + push notification navigation ────────────────
// Fix Audit#Integration-#1 e #2: ascolta eventi dal ponte Java → JavaScript

function NativeBridgeHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Fix Audit#Integration-#2: deep link dal MainActivity Java
    const handleDeepLink = (e: any) => {
      try {
        const { url } = JSON.parse(e?.detail || "{}");
        if (url && typeof url === "string") {
          const path = url
            .replace(/^https?:\/\/(www\.)?rideconnect\.it/, "")
            .replace(/^rideconnect:\/\//, "")
            .replace(/^\//, "");
          navigate(path ? "/" + path : "/", { replace: true });
        }
      } catch {
        /* ignore malformed */
      }
    };
    window.addEventListener("deepLink", handleDeepLink);

    // Fix Audit#Integration-#1: push notification tap navigation
    const handleNavigate = (e: Event) => {
      const { url } = (e as CustomEvent<{ url: string }>).detail || {};
      if (url && typeof url === "string") {
        navigate(url, { replace: true });
      }
    };
    window.addEventListener("rideconnect:navigate", handleNavigate);

    // FIX DEEPLINK (30 Set 2026): gli App Link (https://www.rideconnect.it/...)
    // e gli scheme rideconnect:// erano dichiarati nel manifest ma NESSUNO
    // ascoltava `appUrlOpen` di Capacitor: l'app si apriva sempre sulla route di
    // default e il target veniva ignorato. Inoltriamo l'URL all'handler
    // `deepLink` già esistente (una sola logica di parsing, già coperta dai test).
    const nativeUrlListener = Capacitor.isNativePlatform()
      ? CapacitorApp.addListener("appUrlOpen", (data) => {
          if (data?.url) {
            window.dispatchEvent(
              new CustomEvent("deepLink", {
                detail: JSON.stringify({ url: data.url }),
              }),
            );
          }
        })
      : null;

    // Avvio a freddo da link: in quel caso `appUrlOpen` non scatta e l'URL va
    // letto con getLaunchUrl().
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.getLaunchUrl()
        .then((data) => {
          if (data?.url) {
            window.dispatchEvent(
              new CustomEvent("deepLink", {
                detail: JSON.stringify({ url: data.url }),
              }),
            );
          }
        })
        .catch(() => {
          /* nessun launch URL: caso normale */
        });
    }

    return () => {
      window.removeEventListener("deepLink", handleDeepLink);
      window.removeEventListener("rideconnect:navigate", handleNavigate);
      void nativeUrlListener?.then((l) => l.remove()).catch(() => {});
    };
  }, [navigate]);

  return null; // componente invisibile, solo side-effect
}

// ─── App principale ──────────────────────────────────────────────────────────────────

const Router = Capacitor.isNativePlatform() ? MemoryRouter : BrowserRouter;

function App() {
  const user = useAuthStore((s) => s.user);
  const [maintenance, setMaintenance] = React.useState(false);
  // GOOGLE PLAY QUALITY 2027 (Zero-Tap Sign-In): guardie "una volta per avvio"
  // per il tentativo di ripristino e per la registrazione della restore key.
  const restoreAttemptedRef = useRef(false);
  const restoreKeyRegisteredRef = useRef(false);

  useEffect(() => {
    useAuthStore.getState().initSession();
    // Fix Audit#Integration-#4 e #5: inizializza listener ponte Java → JS
    initGeoListeners();
    // FASE 3: Inizializza device fingerprint per session binding
    initDeviceFingerprint().catch(() => {});

    // FIX SLOWNESS (02 Set 2026): risveglia SUBITO il backend (Render free
    // tier + Neon) con ping fire-and-forget su /health/live e /health/ready.
    // Senza warm-up, le prime chiamate API pagavano il cold start Render
    // (30-60s): l'app sembrava bloccata su spinner infiniti.
    try {
      const API_BASE = resolveApiBaseUrl();
      fetch(`${API_BASE}/health/live`, { cache: "no-store" }).catch(() => {});
      fetch(`${API_BASE}/health/ready`, { cache: "no-store" }).catch(() => {});
    } catch {
      /* warm-up best-effort: mai bloccare il boot */
    }

    // FIX PUSH-DEFAULT (02 Set 2026): chiedi il permesso notifiche al PRIMO
    // avvio (subito dopo l'installazione), così l'utente vede la domanda di
    // sistema e accetta o rifiuta — non serve aspettare il login.
    if (Capacitor.isNativePlatform()) {
      requestNotificationPermissionOnBoot().catch(() => {});
    }

    // FIX-INSETS-API36: attiva il passthrough inset del plugin core SystemBars
    // (Capacitor 8.4+). Senza questa chiamata Capacitor applica solo il padding
    // nativo e azzera le CSS var --safe-area-inset-*; chiamandola su WebView
    // >= 140 vengono iniettate le var REALI e il WebView gestisce env()
    // nativamente (viewport-fit=cover già presente in index.html).
    if (Capacitor.isNativePlatform()) {
      try {
        const iface = (window as any).CapacitorSystemBarsAndroidInterface;
        if (iface && typeof iface.onDOMReady === "function") {
          iface.onDOMReady();
        }
      } catch {
        /* bridge non disponibile: resta attivo il fallback nativo */
      }
    }
  }, []);

  // ── Schermata di manutenzione (backend 503 / DB quota Neon) ───────────────
  // apiFetch segnala i 503 a `maintenanceGuard.ts`, che monta l'overlay solo se
  // più richieste distinte falliscono E il servizio non torna entro pochi
  // secondi (prima compariva per un singolo hiccup risolto dal retry).
  // L'evento `rideconnect:maintenance-clear` (primo 2xx o "Riprova ora") lo
  // rimuove.
  useEffect(() => {
    const onMaintenance = () => setMaintenance(true);
    const onClear = () => setMaintenance(false);
    window.addEventListener("rideconnect:maintenance", onMaintenance);
    window.addEventListener("rideconnect:maintenance-clear", onClear);
    return () => {
      window.removeEventListener("rideconnect:maintenance", onMaintenance);
      window.removeEventListener("rideconnect:maintenance-clear", onClear);
    };
  }, []);

  // ── GOOGLE PLAY QUALITY 2027: Zero-Tap Sign-In ───────────────────────────
  // 1) primo avvio su un NUOVO device e nessuna sessione locale → prova a
  //    ripristinare il login dalla restore key (deve avvenire PRIMA che
  //    l'utente veda la schermata di accesso).
  // 2) utente loggato → assicura che la restore key esista (una volta per
  //    avvio: è idempotente lato server).
  // Entrambe le funzioni degradano in silenzio: nessun impatto se il device
  // non è compatibile o se non c'è alcuna chiave.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    if (user) {
      if (restoreKeyRegisteredRef.current) return;
      restoreKeyRegisteredRef.current = true;
      void registerRestoreKey();
      return;
    }

    if (restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;
    void tryRestoreSession();
  }, [user?.id]);

  // ── GOOGLE PLAY QUALITY 2027: bridge di pressione memoria ────────────────
  // MainActivity.onTrimMemory inoltra il livello con il CustomEvent
  // "rc:trim-memory"; qui svuotiamo le cache JS rigenerabili. Le mappe si
  // iscrivono allo stesso evento per distruggere la surface WebGL quando
  // l'app non è visibile e la memoria scarseggia.
  useEffect(() => {
    const stopMemoryBridge = startMemoryPressureBridge();
    return stopMemoryBridge;
  }, []);

  // ── Capacitor App Lifecycle: gestisce resume da background / screen off ──
  // Fix: Quando l'utente spegne/riaccende lo schermo o cambia app, Android
  // può distruggere la WebView. Questo listener garantisce che la sessione
  // venga ripristinata correttamente al resume.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleAppStateChange = async (state: { isActive: boolean }) => {
      if (state.isActive) {
        // FIX-ANDROID-FONT: Resetta forzatamente il font-size base per prevenire
        // esplosione icone dopo resume su Android WebView
        document.documentElement.style.fontSize = "16px";
        document.body.style.fontSize = "16px";
        // Forza reflow completo del DOM per applicare le dimensioni corrette
        document.body.style.display = "none";
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        document.body.offsetHeight; // trigger reflow
        document.body.style.display = "";

        // FIX BUG #25: Se riding-mode era attiva, rimuovila e riapplicala
        // per forzare il font-size corretto (24px fisso, non accumulato).
        // Questo è l'ultima linea di difesa se il bug del rem si è già
        // manifestato durante il ciclo di screen-off/screen-on.
        const hadRidingMode = document.body.classList.contains("riding-mode");
        if (hadRidingMode) {
          document.body.classList.remove("riding-mode");
          // Forza reflow prima di riapplicare — così il browser riparte da 16px
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          document.body.offsetHeight;
          document.body.classList.add("riding-mode");
          console.log(
            "[App] Reset riding-mode dopo resume per prevenire accumulo font",
          );
        }

        // FIX-LAYOUT-DEFORMATION: ricalcola viewport dopo resume WebView.
        // NOTA: `--vh` custom rimosso (dead code); il resize forzato resta
        // per far ricalcolare al layout le unità svh/dvh dopo lo screen-off.
        window.dispatchEvent(new Event("resize"));

        // FIX-ANDROID-STATE: Ripristina stato salvato se presente
        try {
          const savedState = localStorage.getItem("rc_app_state");
          if (savedState) {
            const parsed = JSON.parse(savedState);
            // Se c'era una registrazione in corso, ripristina
            if (parsed.recordingInProgress) {
              window.dispatchEvent(
                new CustomEvent("rideconnect:restoreRecording", {
                  detail: parsed.recordingData,
                }),
              );
            }
          }
        } catch {
          /* ignore parse errors */
        }

        // App è tornata in foreground — verifica che la sessione sia valida
        const store = useAuthStore.getState();
        if (store.user && !store.accessToken) {
          // Abbiamo user ma non token: prova refresh silenzioso
          try {
            await store.initSession();
          } catch {
            // Fallimento silenzioso — apiFetch gestirà il refresh on-demand
          }
        }
        // Log per debug
        console.log("[App] Resumed — authenticated:", !!store.accessToken);
      } else {
        // App va in background — salva stato critico
        try {
          const recordingState = (window as any).__rideconnect_recording_state;
          if (recordingState) {
            localStorage.setItem(
              "rc_app_state",
              JSON.stringify({
                recordingInProgress: true,
                recordingData: recordingState,
                timestamp: Date.now(),
              }),
            );
          } else {
            localStorage.removeItem("rc_app_state");
          }
        } catch {
          /* ignore */
        }
      }
    };

    CapacitorApp.addListener("appStateChange", handleAppStateChange);

    return () => {
      (CapacitorApp.removeAllListeners as any)?.();
    };
  }, []);

  // FIX-LAYOUT-DEFORMATION: Salva ultima route per ripristino dopo destroy WebView
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const saveRoute = () => {
      try {
        const path = window.location.pathname + window.location.search;
        if (path && path !== "/" && path !== "/login") {
          localStorage.setItem("rc_last_route", path);
        }
      } catch {
        /* ignore */
      }
    };

    // Salva su ogni navigazione
    window.addEventListener("beforeunload", saveRoute);
    window.addEventListener("pagehide", saveRoute);
    // Salva su visibility hidden (app va in background)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") saveRoute();
    });

    return () => {
      window.removeEventListener("beforeunload", saveRoute);
      window.removeEventListener("pagehide", saveRoute);
    };
  }, []);

  // FIX (Luglio 2026): Font-size guard — previene accumulo rem su Android WebView
  // Controlla ogni 3 secondi che il font-size della root sia 16px.
  // Su Android WebView, il computed-style del body può corrompere il rem
  // durante i toggle di riding-mode, causando icone giganti esponenziali.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const GUARD_INTERVAL_MS = 3_000;
    const EXPECTED_ROOT_FONT = "16px";

    const intervalId = setInterval(() => {
      const html = document.documentElement;
      const currentFont =
        html.style.fontSize || getComputedStyle(html).fontSize;

      if (currentFont !== EXPECTED_ROOT_FONT) {
        // Root font-size corrotto — resetta forzatamente
        html.style.fontSize = EXPECTED_ROOT_FONT;

        // Se riding-mode è attiva, riapplica dopo reflow
        if (document.body.classList.contains("riding-mode")) {
          document.body.classList.remove("riding-mode");
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          void document.body.offsetHeight; // force reflow
          document.body.classList.add("riding-mode");
        }
      }
    }, GUARD_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, []);

  // Inizializza FCM push native (Android/iOS) dopo il login
  // FIX CAMBIO ACCOUNT (Ago 2026): oltre all'init, ri-sincronizza il token
  // FCM sul server per l'utente corrente. Senza questo passaggio, con due
  // account sullo stesso dispositivo le notifiche di sistema in background
  // arrivano solo al PRIMO account registrato (l'altro vede solo l'inbox).
  useEffect(() => {
    if (user && isNativePushSupported()) {
      initNativePush()
        .then(() => resyncTokenForCurrentUser())
        .catch((err) => console.error("[App] Native push init failed:", err));
    }
  }, [user?.id]);

  // FASE 3: Pre-carica dati critici nella cache offline dopo il login
  useEffect(() => {
    if (user) {
      preloadCriticalData().catch(() => {});
    }
  }, [user]);

  // SUB-PLAN: fallback propagazione cambio abbonamento.
  // Quando la finestra torna in focus (switch tab/app, ritorno dal telefono),
  // rileggiamo /api/auth/me (max 1 volta ogni 30s) e aggiorniamo lo store.
  // Così anche senza WebSocket (o se l'evento è stato perso) il cambio piano
  // effettuato dall'admin viene recepito senza logout/refresh manuali.
  useEffect(() => {
    if (!user) return;
    let lastSync = 0;
    let inFlight = false;

    const syncSubscription = async () => {
      if (inFlight) return;
      const now = Date.now();
      if (now - lastSync < 30_000) return;
      inFlight = true;
      try {
        const { apiFetch } = await import("@/api/core/apiFetch");
        const me: any = await apiFetch("/api/auth/me").catch(() => null);
        if (!me?.user) return;
        const token = useAuthStore.getState().accessToken;
        if (token) useAuthStore.getState().updateAccess(token, me.user);
        lastSync = Date.now();
      } catch {
        /* best-effort */
      } finally {
        inFlight = false;
      }
    };

    const onFocus = () => {
      syncSubscription();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") syncSubscription();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user?.id]);

  return (
    <Router>
      <NetworkProvider>
        <ErrorBoundary>
          <NativeBridgeHandler />
          <ImpersonationBanner />
          {/* OTT 2026 — ogni cambio pagina riparte dall'inizio (app e browser) */}
          <ScrollToTop />
          <AppWrapper />
          {maintenance && <MaintenanceScreen />}
          {/* OTT 2026 — Sblocco con impronta/Face ID (device fidato) */}
          <BiometricLockGate />
        </ErrorBoundary>
      </NetworkProvider>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="mt-12 md:mt-16"
      />
    </Router>
  );
}

export default App;
